// The start-in rule (20261004090010): a booking belongs to the special date
// its window starts in — the server's match_special_date_event (active dates,
// half-open overlap, earliest start; dates of one service cannot overlap). A
// window that runs past that date's end stays that date's booking, with its
// menus, price and kitchen count, so every booking surface says so in one
// line before the hold is made. Display only: the server decides.
//
// Pure (no React, no Intl of its own): each app passes the time formatter it
// already uses for the venue's clock.

export type SpecialDateSpan = {
  name: string;
  ends_at: string;
};

/**
 * "Runs past the end of Harvest Lunch (15:30) — booked as Harvest Lunch,
 * with its menus and price." when the window ends after its matched date
 * does; null otherwise (no date, the window ends inside it, or a time that
 * does not parse).
 */
export function specialDateOverrunNotice(
  date: SpecialDateSpan | null | undefined,
  windowEndsAt: string | null | undefined,
  formatTime: (iso: string) => string,
): string | null {
  if (!date || !windowEndsAt) return null;
  const dateEnd = new Date(date.ends_at).getTime();
  const windowEnd = new Date(windowEndsAt).getTime();
  if (!Number.isFinite(dateEnd) || !Number.isFinite(windowEnd)) return null;
  if (windowEnd <= dateEnd) return null;
  const name = date.name.trim() || "this special date";
  return `Runs past the end of ${name} (${formatTime(date.ends_at)}) — booked as ${name}, with its menus and price.`;
}

/**
 * The words for special_date_would_change — an edit or move that would
 * change a booking's special date, refused by the server. The server sends
 * the guest sentence as `hint` (and the staff one as `staff_hint`); this is
 * the fallback when a payload arrives without them.
 */
export function specialDateChangeWords(r: {
  hint?: unknown;
  special_date_name?: unknown;
  booked_special_date_name?: unknown;
  [k: string]: unknown;
}): string {
  if (typeof r.hint === "string" && r.hint) return r.hint;
  const into = typeof r.special_date_name === "string" && r.special_date_name ? r.special_date_name : null;
  const outOf =
    typeof r.booked_special_date_name === "string" && r.booked_special_date_name
      ? r.booked_special_date_name
      : null;
  if (into) return `This time falls in ${into}. Remove it and book ${into} instead.`;
  if (outOf) return `This time falls outside ${outOf}. Remove it and book the new time instead.`;
  return "That time belongs to a different special date — remove it and book that date instead.";
}
